import React from 'react';

import {
  faCircle,
  faCircleCheck,
  faCircleQuestion,
  faCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { palette, lightPalette, shape } from '@app/theme';
import { AssessmentTable } from '@components/ui/AnalysisReport/AssessmentTable';
import { ScoreChip } from '@components/ui/AnalysisReport/ScoreChip';
import { TeamMemberCard } from '@components/ui/AnalysisReport/TeamMemberCard';
import { MarkdownContent } from '@components/ui/MarkdownContent';

import type { AnalysisSectionCardProperties } from './AnalysisSectionCard.types';
import type { BulletSentiment } from '@api/aiReviews/api.aiReviews.reviewTypes';


function BulletIcon({ sentiment }: { sentiment: BulletSentiment }) {
  if (sentiment === 'positive') {
    return (
      <FontAwesomeIcon
        icon={faCircleCheck}
        style={{ fontSize: 14, color: palette.success.main, flexShrink: 0, marginTop: 3 }}
      />
    );
  }
  if (sentiment === 'negative') {
    return (
      <FontAwesomeIcon
        icon={faCircleXmark}
        style={{ fontSize: 14, color: palette.error.main, flexShrink: 0, marginTop: 3 }}
      />
    );
  }
  return (
    <FontAwesomeIcon
      icon={faCircle}
      style={{ fontSize: 8, color: lightPalette.text.disabled, flexShrink: 0, marginTop: 5 }}
    />
  );
}

interface IconBulletListProperties {
  items: string[];
  icon: React.ReactNode;
  gap?: number;
  fontSize?: number;
  dataTest?: string;
}

function IconBulletList({ items, icon, gap = 1, fontSize = 13, dataTest }: IconBulletListProperties) {
  return (
    <Box
      component='ul'
      data-testid={dataTest}
      sx={{
        listStyle: 'none',
        p: 0,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        gap,
      }}
    >
      {items.map((item, index) => (
        <Box
          key={index}
          component='li'
          data-testid={dataTest ? `${dataTest}_${index}` : undefined}
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
          {icon}
          <Typography sx={{ fontSize, lineHeight: 1.55, color: lightPalette.text.secondary }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export const AnalysisSectionCard: React.FC<AnalysisSectionCardProperties> = ({
  section,
  sectionNumber,
  teamMembers,
  dataTest,
}) => {
  const hasBullets = section.bullet_points.length > 0;
  const hasAssessment = section.assessment_table.length > 0;
  const hasGaps = section.gaps.length > 0;
  const hasTeam = teamMembers && teamMembers.length > 0;

  return (
    <Paper
      id={`section-${section.id}`}
      data-testid={dataTest}
      elevation={0}
      sx={{
        border: `1px solid ${lightPalette.border.default}`,
        borderRadius: `${shape.borderRadius}px`,
        overflow: 'hidden',
        scrollMarginTop: '96px',
      }}
    >
      {/* Header */}
      <Box
        data-testid={dataTest ? `${dataTest}_header` : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2,
          borderBottom: `1px solid ${lightPalette.border.default}`,
          backgroundColor: lightPalette.background.paper,
        }}
      >
        {/* Section number badge */}
        <Box
          data-testid={dataTest ? `${dataTest}_badge` : undefined}
          sx={{
            width: 32,
            height: 32,
            borderRadius: `${shape.borderRadius}px`,
            backgroundColor: palette.primary.main,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 14,
              fontWeight: 600,
              color: 'primary.contrastText',
              lineHeight: 1,
            }}
          >
            {sectionNumber}
          </Typography>
        </Box>

        {/* Title */}
        <Typography
          data-testid={dataTest ? `${dataTest}_title` : undefined}
          sx={{
            flex: 1,
            fontSize: 17,
            fontWeight: 700,
            color: lightPalette.text.primary,
            letterSpacing: '-0.03em',
          }}
        >
          {section.title}
        </Typography>

        {/* Score chip */}
        <ScoreChip
          score={section.score}
          maxScore={section.max_score}
          height={28}
          fontSize={13}
          labelPx={1.5}
          showBorder
          dataTest={dataTest ? `${dataTest}_score` : undefined}
        />
      </Box>

      {/* Body — always visible */}
      <Box
        data-testid={dataTest ? `${dataTest}_body` : undefined}
        sx={{ px: 3, py: 2.5, backgroundColor: lightPalette.background.paper }}
      >

        {/* 1. Content text (supports markdown) */}
        {section.content && (
          <MarkdownContent
            variant='section'
            dataTest={dataTest ? `${dataTest}_content` : undefined}
            sx={{ mb: (hasBullets || hasAssessment || hasGaps || hasTeam || section.score_rationale) ? 2.5 : 0 }}
          >
            {section.content}
          </MarkdownContent>
        )}

        {/* Bullet points */}
        {hasBullets && (
          <Box
            component='ul'
            data-testid={dataTest ? `${dataTest}_bullets` : undefined}
            sx={{
              listStyle: 'none',
              p: 0,
              m: 0,
              mb: (hasAssessment || hasGaps || section.score_rationale) ? 2.5 : 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {section.bullet_points.map((bp, index) => (
              <Box
                key={index}
                component='li'
                data-testid={dataTest ? `${dataTest}_bullet_${index}` : undefined}
                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
              >
                <BulletIcon sentiment={bp.sentiment} />
                <Typography sx={{ fontSize: 14, lineHeight: 1.6, color: lightPalette.text.secondary }}>
                  {bp.text}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* 2. Assessment table */}
        {hasAssessment && (
          <Box sx={{ mb: (hasGaps || section.score_rationale) ? 2.5 : 0 }}>
            <AssessmentTable
              items={section.assessment_table}
              dataTest={dataTest ? `${dataTest}_assessment` : undefined}
            />
          </Box>
        )}

        {/* 3. Gaps — "Не раскрыто в презентации" */}
        {hasGaps && (
          <Box
            data-testid={dataTest ? `${dataTest}_gaps` : undefined}
            sx={{ mb: section.score_rationale ? 2 : 0 }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: lightPalette.text.muted,
                mb: 1,
              }}
            >
              Не раскрыто в презентации
            </Typography>
            <IconBulletList
              items={section.gaps}
              icon={
                <FontAwesomeIcon
                  icon={faCircleQuestion}
                  style={{ fontSize: 13, color: palette.warning.main, flexShrink: 0, marginTop: 3 }}
                />
              }
              gap={0.75}
              fontSize={13}
              dataTest={dataTest ? `${dataTest}_gaps_list` : undefined}
            />
          </Box>
        )}

        {/* Team members (inside team section) */}
        {hasTeam && (
          <Box
            data-testid={dataTest ? `${dataTest}_team` : undefined}
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 1.5,
              mt: 2.5,
              mb: section.score_rationale ? 2.5 : 0,
            }}
          >
            {teamMembers.map((member, index) => (
              <TeamMemberCard
                key={index}
                member={member}
                dataTest={`${dataTest}_team_member_${index}`}
              />
            ))}
          </Box>
        )}

        {/* 4. Score rationale / summary */}
        {section.score_rationale && (
          <MarkdownContent
            variant='compact'
            dataTest={dataTest ? `${dataTest}_rationale` : undefined}
            sx={{
              fontStyle: 'italic',
              color: lightPalette.text.muted,
              borderTop: `1px solid ${lightPalette.border.light}`,
              pt: 1.5,
              mt: hasGaps ? 1.5 : 0,
            }}
          >
            {section.score_rationale}
          </MarkdownContent>
        )}
      </Box>
    </Paper>
  );
};
